import type {
    CreateTRPCClientOptions,
    HTTPBatchLinkOptions,
    HTTPHeaders,
    HTTPLinkOptions,
} from '@trpc/client';
import { httpBatchLink, httpLink, loggerLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import superjson from 'superjson';

import { AUTHENTICATE_AS } from '../lib/constants.ts';
import type { AppRouter } from '../server/api/root.ts';
import { getLoginAsUserIdForSuperAdmin } from './globals.ts';

const IS_BATCH_ENABLED = false as boolean;

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCReact<AppRouter>();

/**
 * Create tRPC client configuration
 * This is used in the provider setup
 */
export function getTRPCClientConfig(): CreateTRPCClientOptions<AppRouter> {
    const linkOptions: HTTPLinkOptions & HTTPBatchLinkOptions = {
        url: `/api/trpc`,
        headers: async (): Promise<HTTPHeaders> => {
            const headers: HTTPHeaders = {};
            const session = window.Clerk?.session;

            if (session) {
                headers['x-clerk-user-id'] = session.user.id;

                const token = await session.getToken();
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }
            } else if (!AUTHENTICATE_AS) {
                if (window.location.href !== '/sign-in') {
                    window.location.href = '/sign-in';
                }
                throw new Error('No active session found, redirecting to sign-in');
            }

            const impersonate = getLoginAsUserIdForSuperAdmin();
            if (impersonate) {
                headers.loginas = impersonate;
            }

            return headers;
        },
    };
    return {
        /**
         * Transformer used for data de-serialization from the server.
         */
        transformer: superjson,

        /**
         * Links used to determine request flow from client to server.
         */
        links: [
            loggerLink({
                enabled: (opts) =>
                    import.meta.env.DEV || (opts.direction === 'down' && opts.result instanceof Error),
            }),
            IS_BATCH_ENABLED ? httpBatchLink(linkOptions) : httpLink(linkOptions),
        ],
    };
}

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
